import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';
import { ExchangeCancelDialog } from 'components/exchange/exchange-cancel-dialog';
import { useAppState } from 'lib/app-state';
import { useCancelExchange } from 'lib/hooks/use-cancel-exchange';

/**
 * Confirms dropping a ticket change before it happens, either because
 * removing a returned ticket would leave the swap short (the swap takes
 * exactly the tickets being handed back) or because the buyer asked to
 * cancel it directly.
 *
 * Render `exchangeCancelDialog` alongside the cart and call
 * `promptCancelExchange` from whatever would otherwise remove the tickets or
 * from a direct "cancel" action.
 */
export function useExchangeCancelPrompt() {
  const router = useRouter();
  const {
    tempExchangeData
  } = useAppState();
  const cancelExchange = useCancelExchange();
  const [variant, setVariant] = useState<'remove' | 'direct' | null>(null);
  const sourceEventId = tempExchangeData?.sourceEventId;
  const promptCancelExchange = useCallback((promptVariant: 'remove' | 'direct' = 'remove') => setVariant(promptVariant), []);
  const confirmCancelExchange = useCallback(() => {
    setVariant(null);
    cancelExchange();
    // The cart is emptied along with the change, so the buyer lands back on the
    // tickets they were changing rather than on an empty cart.
    router.push(sourceEventId ? `/account/event/${sourceEventId}` : '/');
  }, [cancelExchange, router, sourceEventId]);
  return {
    promptCancelExchange,
    exchangeCancelDialog: <ExchangeCancelDialog open={variant !== null} variant={variant ?? 'remove'} onClose={() => setVariant(null)} onCancelExchange={confirmCancelExchange} />
  };
}